import { BoxProps, CardProps, Center, ChakraProps, Flex, Grid, GridItem, Image, Spinner } from '@chakra-ui/react'
import { Array, Option } from 'effect'
import DetailDisclosurePopover from 'presentation/components/DetailDisclosurePopover'
import { Card, CardPrimaryTitle } from 'presentation/components/molecules/Card'
import { useMobFirstBreakpointValue } from 'presentation/hooks/useMobFirstBreakpointValue'
import CurrentFeaturesSectionViewModel from 'presentation/screens/Billing/CurrentFeaturesSection/CurrentFeaturesSectionViewModel'
import CompsDetailDisclosure from 'presentation/screens/Plans/components/CompsDetailDisclosure'
import SkiptraceDetailDisclosure from 'presentation/screens/Plans/components/SkiptraceDetailDisclosure'
import { fadeIn } from 'presentation/utils/animationClasses'
import { Fragment } from 'react'
import image215 from './images/plan-details-right.215x215.png'
import image430 from './images/plan-details-right.430x430.png'
import image from './images/plan-details-right.png'

export type CurrentFeaturesSectionProps = ChakraProps

const CurrentFeaturesSection = (props: CurrentFeaturesSectionProps) => {
  const vm = CurrentFeaturesSectionViewModel.usePresenter()

  const shouldShowSkiptraceDisclosure = (featureKey: string) =>
    featureKey === 'skiptrace'
    || featureKey === 'bulk-skiptrace'

  const getSkiptraceRateLimitNote = (featureKey: string, hasSplitSkiptraceFeatures: boolean) => {
    if (!hasSplitSkiptraceFeatures) return undefined

    return (
      featureKey === 'skiptrace'
        ? 'Skiptracing is subject to rate limits and as such is not available in bulk lists.'
        : 'Bulk Skiptracing is not subject to skiptracing rate limits.'
    )
  }

  const renderFeatureNameDisclosure = ({
    featureKey,
    featureNameText,
    featureValueTextColor,
    hasSplitSkiptraceFeatures,
  }: {
    featureKey: string
    featureNameText: string
    featureValueTextColor: string
    hasSplitSkiptraceFeatures: boolean
  }) => {
    if (!hasSplitSkiptraceFeatures) return null

    if (featureKey === 'skiptrace' && featureValueTextColor !== 'negative.500') {
      return (
        <DetailDisclosurePopover
          popoverContentProps={{
            w: '160px',
            textAlign: 'center',
          }}
        >
          Skiptracing is subject to rate limits and as such is not available in bulk lists.
        </DetailDisclosurePopover>
      )
    }

    if (featureKey === 'bulk-skiptrace' && featureNameText === 'Bulk Skiptrace') {
      return (
        <DetailDisclosurePopover
          popoverContentProps={{
            w: '160px',
            textAlign: 'center',
          }}
        >
          Bulk Skiptracing is not subject to skiptracing rate limits.
        </DetailDisclosurePopover>
      )
    }

    return null
  }

  return (
    <SectionContainer {...props}>
      {vm.pipe(
        Option.map(v => {
          const hasSplitSkiptraceFeatures = Array.some(
            v,
            feature => feature.featureNameText === 'Bulk Skiptrace',
          )

          return (
            <Fragment key='current-features-section'>
              <DogImage />

              <Flex
                h='full'
                flexDir='column'
                className={fadeIn}
                alignContent='center'
                justifyContent='center'
              >
                <CardPrimaryTitle
                  mb={{
                    base: '3',
                    tabSm: '3',
                    dtLg: '4',
                  }}
                >
                  Plan Details
                </CardPrimaryTitle>

                <Grid
                  mt={{
                    base: '3',
                    tabSm: '3',
                  }}
                  gridTemplateColumns={{
                    base: 'minmax(min-content, 112px) 1fr',
                    tabSm: 'minmax(min-content, 128px) 1fr',
                    dtLg: 'minmax(min-content, 136px) 1fr',
                  }}
                  gridTemplateRows='repeat(3, 1fr)'
                  columnGap={{
                    base: '2',
                    tabSm: '3',
                  }}
                  rowGap={{
                    base: '1',
                    mob: '2',
                    dtSm: '1',
                  }}
                  pb={{ base: '0', tabSm: '1' }}
                >
                  {Array.map(v, feature => (
                    <Grid
                      key={feature.featureNameText}
                      gridTemplateColumns='subgrid'
                      gridColumn='1/-1'
                      display={{
                        base: 'flex',
                        mob: 'grid',
                        tabSm: 'flex',
                        dtSm: 'grid',
                      }}
                      gap={{
                        base: 3,
                        mob: 3,
                        tabSm: 1,
                      }}
                    >
                      <GridItem
                        textStyle={{
                          base: 'body2',
                          mob: 'body',
                          dtLg: 'old.h4',
                        }}
                        color='graystrong.200'
                        whiteSpace='nowrap'
                        display='flex'
                        h='full'
                        alignItems='center'
                        gap='0.5'
                      >
                        {feature.featureNameText}
                        {renderFeatureNameDisclosure({
                          ...feature,
                          hasSplitSkiptraceFeatures,
                        })}
                      </GridItem>
                      <GridItem
                        textStyle={{
                          mobSm: 'body2-b',
                          mob: 'body-b',
                        }}
                        color={feature.featureValueTextColor}
                        display='flex'
                        h='full'
                        alignItems='center'
                        gap='0.5'
                      >
                        {feature.featureValueText}
                        {feature.disclosure.pipe(
                          Option.flatMap(value => {
                            if (feature.featureNameText === 'Comps') {
                              return Option.some(
                                <CompsDetailDisclosure
                                  propertyCount={value}
                                />,
                              )
                            }

                            if (shouldShowSkiptraceDisclosure(feature.featureKey)) {
                              return Option.some(
                                <SkiptraceDetailDisclosure
                                  propertyCount={value}
                                  rateLimitNote={getSkiptraceRateLimitNote(feature.featureKey, hasSplitSkiptraceFeatures)}
                                  popoverContentProps={{ w: '200px' }}
                                />,
                              )
                            }

                            return Option.none()
                          }),
                          Option.getOrNull,
                        )}
                      </GridItem>
                    </Grid>
                  ))}
                </Grid>
              </Flex>
            </Fragment>
          )
        }),
        // @NOTE: should it be a view model loading state instead of option?
        Option.getOrElse(() => (
          <Center
            h='full'
            flex='1'
          >
            <Spinner color='graystrong.500' />
          </Center>
        )),
      )}
    </SectionContainer>
  )
}

export default CurrentFeaturesSection

const SectionContainer = ({ children, ...props }: CardProps) => (
  <Card
    variant='default'
    colorScheme='card.bg.1'
    size={{
      base: 'xs-locked',
      mob: 'sm-locked',
      dtLg: 'md-locked',
    }}
    pos='relative'
    minH='253px'
    w='full'
    {...props}
  >
    {children}
  </Card>
)

const DogImage = () => {
  const { mfv } = useMobFirstBreakpointValue()
  return (
    <Image
      src={image215}
      srcSet={`${image215} 1x, ${image430} 2x, ${image} 3x`}
      pos='absolute'
      {...mfv<BoxProps>({
        dtLg: {
          w: '200px',
          top: '-6',
          right: '0',
        },
        tabSm: {
          w: '42%',
          top: '-4.5',
          right: '-3',
        },
        mob: {
          w: '35%',
          maxW: '200px',
          top: '-3',
          right: '0',
        },
        mobSm: {
          w: '40%',
          top: '-4',
          right: '0',
        },
      })}
    />
  )
}
